import type { CaregiverAvailability, ComplianceView, Relationships } from '@homecare/contracts';
import { hoursText } from '@homecare/domain';
import { Card, Eyebrow, Spinner, StatusPill } from '@homecare/ui';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { useApi } from '../../lib/api-hooks';

/**
 * Can they take a shift? Three answers, side by side, at the top of the record
 * (slice-02 session 8).
 *
 * ## Sections, not tabs
 *
 * A coordinator deciding whether somebody can take a shift needs three things
 * at once: whether their credentials let them work, when they have said they
 * are free, and whether they must not be sent to this client. Behind tabs the
 * third click is the exclusion — and the exclusion is the one of the three
 * that nobody can override. A scheduler may override a credential block or a
 * conflict with a reason on record; nobody overrides "the family asked that
 * this aide never come back".
 *
 * So the record is one page of sections, and this strip states the three
 * answers above them, each linking to its section. It reads the same queries
 * the sections below read — same keys, same endpoints — so the strip and the
 * section can never be two answers, and opening the record costs no extra
 * request.
 *
 * Every exclusion is listed here **with its reason**, in full. A count would
 * send the coordinator scrolling for the one fact that settles the question.
 */
export function ShiftReadiness({ caregiverId }: { caregiverId: string }): ReactNode {
  const api = useApi();

  // The CompliancePanel's own key for "today": its date box starts empty.
  const compliance = useQuery({
    queryKey: ['caregivers', 'compliance', caregiverId, ''],
    queryFn: () => api.get<ComplianceView>(`/v1/caregivers/${caregiverId}/compliance`),
    staleTime: 0,
  });
  const availability = useQuery({
    queryKey: ['caregivers', 'availability', caregiverId],
    queryFn: () => api.get<CaregiverAvailability>(`/v1/caregivers/${caregiverId}/availability`),
    staleTime: 0,
  });
  const relationships = useQuery({
    queryKey: ['caregivers', 'relationships', caregiverId],
    queryFn: () => api.get<Relationships>(`/v1/caregivers/${caregiverId}/client-relationships`),
    staleTime: 0,
  });

  return (
    <Card>
      <h2 className="text-ink text-lg font-bold">Can they take a shift?</h2>
      <div className="mt-4 grid gap-4 md:grid-cols-3" data-testid="shift-readiness">
        <Panel title="Credentials" href="#compliance" loading={compliance.isPending}>
          {compliance.data &&
            (compliance.data.compliant ? (
              <>
                <StatusPill tone="verified">Can work today</StatusPill>
                {compliance.data.warnings.length > 0 && (
                  <p className="text-ink-3 mt-2 text-sm">
                    {compliance.data.warnings.length === 1
                      ? '1 credential worth chasing.'
                      : `${compliance.data.warnings.length} credentials worth chasing.`}
                  </p>
                )}
              </>
            ) : (
              <>
                <StatusPill tone="critical">Cannot work today</StatusPill>
                <ul className="text-ink-2 mt-2 grid gap-1 text-sm">
                  {compliance.data.blocking.map((issue) => (
                    <li key={issue.credentialTypeId}>{issue.message}</li>
                  ))}
                </ul>
              </>
            ))}
        </Panel>

        <Panel title="Rota" href="#availability" loading={availability.isPending}>
          {availability.data && <RotaSummary data={availability.data} />}
        </Panel>

        <Panel title="Do not send" href="#restrictions" loading={relationships.isPending}>
          {relationships.data && <Exclusions data={relationships.data} />}
        </Panel>
      </div>
    </Card>
  );
}

function Panel({
  title,
  href,
  loading,
  children,
}: {
  title: string;
  href: string;
  loading: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="border-line grid content-start gap-2 rounded-md border p-3">
      <div className="flex items-baseline justify-between gap-2">
        <Eyebrow>{title}</Eyebrow>
        <a href={href} className="text-brand text-xs font-medium hover:underline">
          Details
        </a>
      </div>
      {loading ? (
        <p className="text-ink-3 flex items-center gap-2 text-sm" role="status">
          <Spinner /> Checking…
        </p>
      ) : (
        <div>{children}</div>
      )}
    </section>
  );
}

function RotaSummary({ data }: { data: CaregiverAvailability }): ReactNode {
  const minutes = data.windows.reduce((sum, window) => {
    const [startHour, startMinute] = window.startTime.split(':').map(Number);
    const [endHour, endMinute] =
      window.endTime === '24:00' ? [24, 0] : window.endTime.split(':').map(Number);
    return sum + (endHour! * 60 + endMinute!) - (startHour! * 60 + startMinute!);
  }, 0);
  const next = data.windows[0];
  const pending = data.timeOff.filter((request) => request.status === 'REQUESTED').length;

  if (data.windows.length === 0) {
    return (
      <p className="text-ink text-sm">
        Not free at any time from {data.from} to {data.to}.
      </p>
    );
  }

  return (
    <div className="grid gap-1 text-sm">
      <p className="text-ink">
        <span className="font-semibold">{hoursText(minutes)}</span> free from {data.from} to{' '}
        {data.to}.
      </p>
      {next && (
        <p className="text-ink-2">
          Next: {next.date}, {next.startTime}–{next.endTime === '24:00' ? 'midnight' : next.endTime}
        </p>
      )}
      {pending > 0 && (
        <p className="text-ink-3">
          {pending === 1
            ? '1 time-off request waiting, still counted as free.'
            : `${pending} time-off requests waiting, still counted as free.`}
        </p>
      )}
    </div>
  );
}

function Exclusions({ data }: { data: Relationships }): ReactNode {
  const excluded = data.restrictions.filter((restriction) => restriction.type === 'EXCLUDED');

  if (excluded.length === 0) {
    return <p className="text-ink text-sm">No client has asked not to have them.</p>;
  }

  return (
    <ul className="grid gap-2 text-sm" aria-label="Clients this caregiver must not be sent to">
      {excluded.map((restriction) => (
        <li key={restriction.id} className="grid gap-0.5">
          <span className="text-ink font-semibold">
            {restriction.clientName ?? 'A client you don’t have access to'}
          </span>
          <span className="text-ink-2">{restriction.reason}</span>
        </li>
      ))}
      <li className="text-ink-3 text-xs">A hard block: the scheduler cannot override it.</li>
    </ul>
  );
}
